// src/helpers/roleControl.ts
// Simplified role control based on User.isMaster and UserCompanyMembership.role.code
// Role codes: 'admin' = Company Admin, 'user' = Standard User, 'viewer' = View Only

import { useAuth } from '../context/AuthContext';

/**
 * Resolve the user's effective role code
 * Prioritizes currentCompanyRole (set by CompanySelection, preserved by AuthContext)
 */
const resolveUserRoleCode = (user: any): string | undefined => {
    if (!user) return undefined;

    // Primary: currentCompanyRole (set by CompanySelection, preserved by AuthContext refresh)
    if (user.currentCompanyRole?.code) {
        return user.currentCompanyRole.code;
    }

    // Fallback: find role from companyMemberships array
    const memberships = user?.companyMemberships;
    if (Array.isArray(memberships) && memberships.length > 0) {
        const activeCompanyId = user?.currentCompanyId ?? user?.companyId;
        const activeMembership =
            memberships.find((membership: any) => membership?.company?.id === activeCompanyId) || memberships[0];

        if (activeMembership?.role?.code) {
            return activeMembership.role.code;
        }
    }

    return undefined;
};

/**
 * Checks if the current user has one of the accepted role codes
 * 
 * @param acceptedRoles Array of role codes that are allowed access (e.g. ['admin', 'user'])
 * @returns boolean indicating whether the user has permission
 */
export const roleControl = (acceptedRoles: string[]): boolean => {
    // Retrieve the user's role from local storage
    const storedUser = localStorage.getItem('currentUser');
    if (!storedUser) {
        return false; // If there's no user stored, return false
    }

    try {
        const user = JSON.parse(storedUser);
        
        // Master users have access to everything
        if (user?.isMaster === true) {
            return true;
        }
        
        const effectiveRoleCode = resolveUserRoleCode(user);

        // If acceptedRoles is empty or not provided, allow access by default
        if (!acceptedRoles || acceptedRoles.length === 0) {
            return true;
        }

        if (!effectiveRoleCode) {
            return false;
        }

        // Check if the user's role code is in the acceptedRoles array
        return acceptedRoles.includes(effectiveRoleCode);
    } catch (error) {
        console.error('Error parsing user data:', error);
        return false;
    }
};

/**
 * Hook version of roleControl that gets the current user from context
 * instead of localStorage for improved reliability
 * 
 * @param acceptedRoles Array of role codes that are allowed access (e.g. ['admin', 'user'])
 * @returns boolean indicating whether the user has permission
 */
export const useRoleControl = (acceptedRoles: string[]) => {
    const { currentUser } = useAuth();
    
    // If no user is logged in, deny access
    if (!currentUser) {
        return false;
    }
    
    // Master users have access to everything
    if (currentUser?.isMaster === true) {
        return true;
    }
    
    const effectiveRoleCode = resolveUserRoleCode(currentUser);

    // If no accepted roles are specified, allow access by default
    if (!acceptedRoles || acceptedRoles.length === 0) {
        return true;
    }
    
    // Check if the user's role code is in the accepted roles
    return !!effectiveRoleCode && acceptedRoles.includes(effectiveRoleCode);
};

/**
 * Checks if a specific role code has permission
 * 
 * @param roleCode The role code to check (e.g. 'admin', 'user', 'viewer')
 * @param acceptedRoles Array of role codes that are allowed access
 * @returns boolean indicating whether the role has permission
 */
export const hasRolePermission = (roleCode: string | undefined, acceptedRoles: string[]): boolean => {
    // If no role code provided, deny access
    if (!roleCode) {
        return false;
    }
    
    // If no accepted roles are specified, allow access by default
    if (!acceptedRoles || acceptedRoles.length === 0) {
        return true;
    }
    
    // Check if the role code is in the accepted roles
    return acceptedRoles.includes(roleCode);
};

/**
 * Check if the current user is a master user
 * 
 * @returns boolean indicating if user is master
 */
export const isMaster = (): boolean => {
    const storedUser = localStorage.getItem('currentUser');
    if (!storedUser) {
        return false;
    }

    try {
        const user = JSON.parse(storedUser);
        return user?.isMaster === true;
    } catch (error) {
        console.error('Error parsing user data:', error);
        return false;
    }
};

/**
 * Hook to check if current user is a master user
 */
export const useIsMaster = (): boolean => {
    const { currentUser } = useAuth();
    return currentUser?.isMaster === true;
};
